import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { QuestionnaireBase } from '../../interfaces/questionnaire-base.interface';
import { GpuFormFieldAppearance } from '@geneplanet/ngx-ui/src/lib/form-field';
import {
  HabitsConfiguration,
  HabitsQuestion,
} from 'src/app/analyses/interfaces/analyses.types';
import {
  ControlContainer,
  FormControl,
  Validators,
  ReactiveFormsModule,
  FormRecord,
} from '@angular/forms';
import { controlContainerProvider } from '../../constants/control-container-provider';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SliderComponent } from 'src/app/shared/components/slider/slider.component';
import { startWith } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { AnswerDict } from '../../interfaces/answer-dict.interface';
import { AnalyticsService } from '@geneplanet/ngx-utils/src/lib/analytics';
import { AnswerDto } from '../../interfaces/answer-dto.interface';
import { GpuRadioGroupModule } from '@geneplanet/ngx-ui/src/lib/radio-group';

@UntilDestroy()
@Component({
  selector: 'app-physical-questionnaire',
  standalone: true,
  imports: [
    CommonModule,
    GpuRadioGroupModule,
    ReactiveFormsModule,
    SliderComponent,
    TranslateModule,
  ],
  templateUrl: './physical-questionnaire.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [controlContainerProvider],
})
export class PhysicalQuestionnaireComponent
  implements QuestionnaireBase, OnDestroy
{
  public questionnaire: HabitsConfiguration;
  public formFieldAppearance: GpuFormFieldAppearance;
  public answers: AnswerDict;
  public smallTitle: boolean;

  private parentContainer = inject(ControlContainer);
  private analyticsService = inject(AnalyticsService);

  get form() {
    return this.parentContainer.control as FormRecord;
  }

  ngOnDestroy() {
    this.questionnaire.questions.forEach((question) => {
      this.form.removeControl(question.id);
    });
  }

  public init(): void {
    this.questionnaire.questions.forEach((question) => {
      const value = this.getInitialValue(question);

      this.form.addControl(
        question.id,
        new FormControl(value, [Validators.required, Validators.min(0)])
      );
    });

    const questions = this.questionnaire.questions;
    this.toggleRelated(questions[0].id, questions[1].id);
    this.toggleRelated(questions[2].id, questions[3].id);
  }

  public onSubmit(): AnswerDict {
    return Object.entries(this.form.getRawValue()).reduce(
      (prev, [key, value]) => {
        if (key === 'date') {
          return prev;
        }

        let answer = {};
        if (isNaN(value)) {
          // For Options selected value is answer ID
          answer = { id: value };
        } else {
          // For Slider selected value is value
          const question = this.questionnaire.questions.find(
            (q) => q.id === key
          );
          answer = { id: question.answers[0].id, value };
        }

        return {
          ...prev,
          [key]: [answer],
        };
      },
      {}
    );
  }

  protected track(questionId: string, answerId?: string): void {
    let data = {
      id: this.form.get(questionId).value,
    } as AnswerDto;
    if (answerId) {
      data = {
        id: answerId,
        value: String(this.form.get(questionId).value),
      };
    }

    this.analyticsService.trackFormValue(
      'analyses-add',
      'value',
      data,
      this.questionnaire.id,
      questionId
    );
  }

  private toggleRelated(sliderId: string, optionsId: string) {
    this.form
      .get(sliderId)
      .valueChanges.pipe(
        startWith(this.form.get(sliderId).value),
        untilDestroyed(this)
      )
      .subscribe((value) => {
        const control = this.form.get(optionsId);
        if (value <= 0 && !control.disabled) {
          control.disable();
        } else if (value > 0 && control.disabled) {
          control.enable();
        }
      });
  }

  private getInitialValue(question: HabitsQuestion): string | number {
    if (Object.keys(this.answers).length) {
      const answer = this.answers[question.id][0];

      if (Object.hasOwn(answer, 'value')) {
        return answer.value;
      }

      return answer.id;
    }
    return question.answers.length > 1 ? question.answers[0].id : -1;
  }
}
